import React from "react";
import { motion, Variants } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { NotificationBadge } from "@/components/shared/NotificationBadge";

interface ModuleCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  className?: string;
  onClick?: () => void;
  notificationCount?: number;
  index?: number;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  icon,
  href,
  className,
  onClick,
  notificationCount,
}) => {
  const { goTo } = useNavigationHelpers();

  const handleClick = () => {
    if (onClick) onClick();
    else goTo(href);
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 24, scale: 0.97 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 18,
        mass: 0.9,
      },
    },
  };

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -4, scale: 1.015 }}
      whileTap={{ scale: 0.975 }}
      transition={{ type: "spring", stiffness: 420, damping: 28 }}
      className={cn("h-full", className)}
    >
      <div
        role="link"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
        className={cn(
          "group relative flex h-full flex-col justify-between",
          // Base visual
          "rounded-2xl border border-border/40 bg-card/60 p-5 shadow-sm backdrop-blur-sm",
          // Hover: apenas cores e sombra — Framer cuida do transform
          "hover:shadow-primary/8 hover:border-primary/35 hover:shadow-lg",
          "transition-colors duration-200",
          "cursor-pointer overflow-hidden",
        )}
      >
        {/* Gradiente sutil de fundo no hover */}
        <div className="duration-400 pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/[0.06] via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

        {/* Sheen: brilho diagonal que cruza o card no hover */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
          <div className="absolute inset-y-0 -left-full w-2/3 -skew-x-12 bg-gradient-to-r from-transparent via-primary/10 to-transparent opacity-0 transition-all duration-700 ease-out group-hover:left-full group-hover:opacity-100" />
        </div>

        {/* Topo: Ícone + Badge + Seta */}
        <div className="relative z-10 mb-4 flex items-start justify-between">
          {/* Ícone com badge */}
          <div className="relative">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-xl",
                "bg-primary/10 text-primary",
                "group-hover:bg-primary group-hover:text-primary-foreground",
                "group-hover:shadow-md group-hover:shadow-primary/25",
                "group-hover:-rotate-3 group-hover:scale-105",
                "transition-all duration-200 ease-out",
              )}
            >
              {React.cloneElement(icon as React.ReactElement, {
                className: "h-5 w-5 transition-transform duration-200",
              })}
            </div>

            {notificationCount !== undefined && notificationCount > 0 && (
              <motion.div
                initial={{ scale: 0, rotate: -15 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 20, delay: 0.3 }}
                className="pointer-events-none absolute -right-2 -top-2 z-20"
              >
                <NotificationBadge
                  count={notificationCount}
                  className="border-2 border-background shadow-sm"
                />
              </motion.div>
            )}
          </div>

          {/* Seta de navegação */}
          <div
            className={cn(
              "rounded-lg p-1.5",
              "group-hover:bg-primary/8 text-muted-foreground/40 group-hover:text-primary",
              "transition-colors duration-200",
            )}
          >
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </div>
        </div>

        {/* Conteúdo textual */}
        <div className="relative z-10 space-y-1">
          <h3
            className={cn(
              "text-[15px] font-semibold leading-snug tracking-tight text-foreground",
              "transition-colors duration-200 group-hover:text-primary",
            )}
          >
            {title}
          </h3>
          <p className="line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default ModuleCard;
